import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Settings, Save, Users, Loader2, Waves, X, MousePointerClick, RotateCcw, Eye, Clock, Zap, Calendar, PlayCircle } from 'lucide-react';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;

/* ---------- Role / Zone palette ---------- */
const ZONE_THEME = {
  1: { perf: 'bg-blue-600 border-blue-300 text-blue-50', warmup: 'bg-amber-700 border-amber-400 text-amber-50', transit: 'bg-purple-700 border-purple-400 text-purple-50' },
  2: { perf: 'bg-emerald-600 border-emerald-300 text-emerald-50', warmup: 'bg-orange-700 border-orange-400 text-orange-50', transit: 'bg-fuchsia-700 border-fuchsia-400 text-fuchsia-50' },
};
const ROLE_LABEL = (t) => ({
  perf: (t && t('performance')) || 'PERF',
  warmup: (t && t('warmup')) || 'WARMUP',
  transit: (t && t('transit')) || 'TRANSIT',
});

const FIELD_KEY = {
  perf: 'performance_lane',
  warmup: 'warmup_lane',
  transit: 'transit_lane',
};

/* ---------- Draggable role chip ---------- */
const DraggableRoleChip = ({ zoneNumber, role, label, currentLane, onClear, isDragging, onDragStart, onDragEnd }) => {
  const cls = ZONE_THEME[zoneNumber][role];
  return (
    <div
      draggable
      onDragStart={(e) => onDragStart(e, zoneNumber, role)}
      onDragEnd={onDragEnd}
      className={`inline-flex items-center gap-1.5 rounded-md border-2 px-2.5 py-1.5 text-[11px] font-bold shadow cursor-grab active:cursor-grabbing select-none transition-all ${cls} ${isDragging ? 'opacity-50 scale-95' : 'hover:scale-105'}`}
      data-testid={`role-chip-z${zoneNumber}-${role}`}
    >
      <MousePointerClick size={10} className="opacity-70" />
      <span>Z{zoneNumber} · {label}</span>
      {currentLane != null && (
        <span className="bg-black/30 rounded px-1 py-0.5 text-[10px] font-mono">L{currentLane}</span>
      )}
      {currentLane != null && (
        <button
          onClick={(e) => { e.stopPropagation(); onClear(); }}
          className="ml-0.5 hover:bg-black/40 rounded p-0.5"
          data-testid={`clear-role-z${zoneNumber}-${role}`}
        >
          <X size={10} />
        </button>
      )}
    </div>
  );
};

/* ---------- Pool lane (drop zone) ---------- */
const PoolLane = ({ laneNumber, assignments, onDrop, onDragOver, onClearAll, isHighlighted, t }) => {
  const primary = assignments[0];
  const cls = primary
    ? ZONE_THEME[primary.zone][primary.role]
    : 'bg-slate-800/40 border-slate-700/50 text-slate-300';
  return (
    <div
      onDragOver={(e) => { e.preventDefault(); onDragOver(laneNumber); }}
      onDragLeave={() => onDragOver(null)}
      onDrop={(e) => { e.preventDefault(); onDrop(laneNumber); }}
      className={`relative flex items-center gap-2 px-3 py-2.5 rounded-lg border-2 transition-all ${cls} ${isHighlighted ? 'ring-2 ring-cyan-400 scale-[1.02] shadow-lg shadow-cyan-500/30' : ''}`}
      data-testid={`pool-lane-${laneNumber}`}
    >
      <span className="text-xs font-bold opacity-80 w-14 select-none">{(t && t('lane')) || 'Lane'} {laneNumber}</span>
      <div className="flex-1 flex flex-wrap gap-1.5">
        {assignments.length === 0 && (
          <span className="text-xs italic opacity-50 select-none">{(t && t('dropRoleHere')) || 'drop a role here'}</span>
        )}
        {assignments.map((a, k) => (
          <Badge key={k} className={`text-[10px] py-0 ${ZONE_THEME[a.zone][a.role]} border-none`}>
            Z{a.zone} · {a.label}
          </Badge>
        ))}
      </div>
      {assignments.length > 0 && (
        <button
          onClick={onClearAll}
          className="text-xs opacity-60 hover:opacity-100 hover:bg-black/30 rounded p-1"
          title={(t && t('clearLane')) || 'Clear lane'}
          data-testid={`clear-lane-${laneNumber}`}
        >
          <X size={12} />
        </button>
      )}
    </div>
  );
};

const ZoneConfigModal = ({ open, onClose, competition, users = [], onSaved, t }) => {
  const totalLanes = parseInt(competition?.lanes) || 4;
  const poolLength = parseInt(competition?.pool_length) || 25;
  const poolWidth = parseInt(competition?.pool_width) || 25;
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [zoneCount, setZoneCount] = useState(1);
  const [zones, setZones] = useState([
    { zone_number: 1, performance_lane: 1, warmup_lane: null, transit_lane: null, assigned_user_ids: [] }
  ]);
  // Drag state
  const [draggingFrom, setDraggingFrom] = useState(null); // {zoneNumber, role}
  const [hoverLane, setHoverLane] = useState(null);

  // Preview state
  const [showPreview, setShowPreview] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [previewEventType, setPreviewEventType] = useState('');
  const [availableEventTypes, setAvailableEventTypes] = useState([]);

  // Create-session state
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createForm, setCreateForm] = useState({
    name: '',
    start_date: '',
    start_time: '',
    interval_duration: 6,
    preparation_duration: 45,
  });

  // Load existing config
  useEffect(() => {
    if (!open || !competition?.id) return;
    setLoading(true);
    setError('');
    setShowPreview(false);
    setPreviewData(null);
    setShowCreateForm(false);
    // Default start_date to competition.start_date or today
    const defaultDate = (competition?.start_date || new Date().toISOString()).slice(0, 10);
    setCreateForm({
      name: '',
      start_date: defaultDate,
      start_time: '14:00',
      interval_duration: 6,
      preparation_duration: 45,
    });
    Promise.all([
      axios.get(`${API_BASE_URL}/api/competitions/${competition.id}/zone-config`),
      axios.get(`${API_BASE_URL}/api/registrations`).catch(() => ({ data: { registrations: [] } })),
    ])
      .then(([cfgRes, regsRes]) => {
        const cfg = cfgRes.data.zone_config || {};
        setZoneCount(cfg.zone_count || 1);
        setZones(cfg.zones || [{ zone_number: 1, performance_lane: 1, warmup_lane: null, transit_lane: null, assigned_user_ids: [] }]);
        // Distinct event_type_codes registered for this competition
        const codes = Array.from(new Set(
          (regsRes.data.registrations || [])
            .filter(r => r.competition_id === competition.id)
            .map(r => r.event_type_code)
        )).sort();
        setAvailableEventTypes(codes);
        if (codes.length > 0) setPreviewEventType(codes[0]);
      })
      .catch((e) => setError('Failed to load configuration: ' + (e.response?.data?.detail || e.message)))
      .finally(() => setLoading(false));
  }, [open, competition?.id]);

  // Sync zones with zone count
  useEffect(() => {
    setZones(prev => {
      if (zoneCount === 1) {
        return [prev[0] || { zone_number: 1, performance_lane: 1, warmup_lane: null, transit_lane: null, assigned_user_ids: [] }];
      }
      const z1 = prev[0] || { zone_number: 1, performance_lane: 1, warmup_lane: null, transit_lane: null, assigned_user_ids: [] };
      const z2 = prev[1] || { zone_number: 2, performance_lane: 2, warmup_lane: null, transit_lane: null, assigned_user_ids: [] };
      return [{ ...z1, zone_number: 1 }, { ...z2, zone_number: 2 }];
    });
  }, [zoneCount]);

  /* ---------- Drag handlers ---------- */
  const onDragStart = (e, zoneNumber, role) => {
    setDraggingFrom({ zoneNumber, role });
    e.dataTransfer.effectAllowed = 'move';
    try { e.dataTransfer.setData('text/plain', `z${zoneNumber}:${role}`); } catch {}
  };
  const onDragEnd = () => { setDraggingFrom(null); setHoverLane(null); };

  const onDropToLane = (laneNumber) => {
    if (!draggingFrom) return;
    const { zoneNumber, role } = draggingFrom;
    const fieldKey = FIELD_KEY[role];
    setZones(prev => prev.map(z => {
      // If another role on the SAME zone uses this lane, clear it (reassignment)
      const sameZone = z.zone_number === zoneNumber;
      const updated = { ...z };
      if (sameZone) {
        if (z.performance_lane === laneNumber && fieldKey !== 'performance_lane') updated.performance_lane = null;
        if (z.warmup_lane === laneNumber && fieldKey !== 'warmup_lane') updated.warmup_lane = null;
        if (z.transit_lane === laneNumber && fieldKey !== 'transit_lane') updated.transit_lane = null;
        updated[fieldKey] = laneNumber;
      } else {
        // If the target lane is the OTHER zone's performance lane, refuse (perf must be unique).
        // We just don't assign in that case — visual feedback elsewhere.
        if (fieldKey === 'performance_lane' && (z.performance_lane === laneNumber || z.warmup_lane === laneNumber || z.transit_lane === laneNumber)) {
          // Don't auto-clear other zone — user must explicitly do so for perf conflict
          // For non-perf roles, sharing is allowed.
        }
      }
      return updated;
    }));
    // Validate perf uniqueness post-drop
    setTimeout(() => {
      setZones(prev => {
        const perfLanes = prev.map(z => z.performance_lane).filter(l => l != null);
        const dupes = perfLanes.filter((l, i) => perfLanes.indexOf(l) !== i);
        if (dupes.length > 0) {
          setError((t && t('perfLaneConflict')) || 'Performance lane must be unique per zone. Move conflicting role to a different lane.');
        } else {
          setError('');
        }
        return prev;
      });
    }, 0);
    setDraggingFrom(null);
    setHoverLane(null);
  };

  const clearRole = (zoneNumber, role) => {
    const fieldKey = FIELD_KEY[role];
    setZones(prev => prev.map(z => z.zone_number === zoneNumber ? { ...z, [fieldKey]: null } : z));
    setError('');
  };

  const clearLane = (laneNumber) => {
    setZones(prev => prev.map(z => ({
      ...z,
      performance_lane: z.performance_lane === laneNumber ? null : z.performance_lane,
      warmup_lane: z.warmup_lane === laneNumber ? null : z.warmup_lane,
      transit_lane: z.transit_lane === laneNumber ? null : z.transit_lane,
    })));
    setError('');
  };

  const resetAll = () => {
    setZones(prev => prev.map(z => ({ ...z, performance_lane: null, warmup_lane: null, transit_lane: null })));
    setError('');
  };

  const toggleUser = (zoneIdx, userId) => {
    setZones(prev => prev.map((z, i) => {
      if (i !== zoneIdx) return z;
      const list = z.assigned_user_ids || [];
      return { ...z, assigned_user_ids: list.includes(userId) ? list.filter(u => u !== userId) : [...list, userId] };
    }));
  };

  /* ---------- Lane → assignments map ---------- */
  const laneAssignments = useMemo(() => {
    const map = {}; // lane -> [{zone, role, label}]
    const labels = ROLE_LABEL(t);
    zones.forEach(z => {
      ['perf', 'warmup', 'transit'].forEach(role => {
        const lane = z[FIELD_KEY[role]];
        if (lane != null && lane > 0) {
          map[lane] = (map[lane] || []).concat({ zone: z.zone_number, role, label: labels[role] });
        }
      });
    });
    return map;
  }, [zones, t]);

  const runPreview = async () => {
    if (!previewEventType) {
      setError((t && t('selectEventToPreview')) || 'Select an event type to preview.');
      return;
    }
    const perfLanes = zones.map(z => z.performance_lane);
    if (perfLanes.some(l => l == null)) {
      setError((t && t('perfLaneRequired')) || 'Each zone must have a performance lane.');
      return;
    }
    setPreviewLoading(true);
    setError('');
    try {
      const payload = {
        event_type_code: previewEventType,
        zone_count: zoneCount,
        zones: zones.map(z => ({
          zone_number: z.zone_number,
          performance_lane: parseInt(z.performance_lane),
          warmup_lane: z.warmup_lane == null ? null : parseInt(z.warmup_lane),
          transit_lane: z.transit_lane == null ? null : parseInt(z.transit_lane),
        })),
      };
      const res = await axios.post(`${API_BASE_URL}/api/competitions/${competition.id}/preview-running-order`, payload);
      setPreviewData(res.data);
      setShowPreview(true);
    } catch (e) {
      setError('Preview failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setPreviewLoading(false);
    }
  };

  // Group preview passages by OT for visual timeline
  const previewByOT = useMemo(() => {
    if (!previewData?.passages) return [];
    const map = new Map();
    previewData.passages.forEach(p => {
      if (!map.has(p.official_top_time)) map.set(p.official_top_time, []);
      map.get(p.official_top_time).push(p);
    });
    return Array.from(map.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([ot, athletes]) => ({ ot, athletes: athletes.sort((a, b) => a.zone_number - b.zone_number) }));
  }, [previewData]);

  /**
   * Create the live session: persists zone_config first, then creates the event session.
   */
  const createSession = async () => {
    if (!createForm.name?.trim()) {
      setError((t && t('sessionNameRequired')) || 'Session name is required.');
      return;
    }
    if (!createForm.start_date || !createForm.start_time) {
      setError((t && t('sessionDateTimeRequired')) || 'Start date and time are required.');
      return;
    }
    const perfLanes = zones.map(z => z.performance_lane);
    if (perfLanes.some(l => l == null)) {
      setError((t && t('perfLaneRequired')) || 'Each zone must have a performance lane.');
      return;
    }
    setCreating(true);
    setError('');
    try {
      // 1) Persist zone config so create_event_session reads it
      const cfgPayload = {
        zone_count: zoneCount,
        zones: zones.map(z => ({
          zone_number: z.zone_number,
          performance_lane: parseInt(z.performance_lane),
          warmup_lane: z.warmup_lane == null ? null : parseInt(z.warmup_lane),
          transit_lane: z.transit_lane == null ? null : parseInt(z.transit_lane),
          assigned_user_ids: z.assigned_user_ids || [],
        })),
      };
      await axios.put(`${API_BASE_URL}/api/competitions/${competition.id}/zone-config`, cfgPayload);

      // 2) Build start_time ISO and create the session
      const startISO = `${createForm.start_date}T${createForm.start_time}:00`;
      const sessionPayload = {
        competition_id: competition.id,
        event_type_code: previewEventType,
        start_time: startISO,
        name: createForm.name.trim(),
        interval_duration: parseInt(createForm.interval_duration) || 6,
        preparation_duration: parseInt(createForm.preparation_duration) || 45,
      };
      const res = await axios.post(`${API_BASE_URL}/api/event-sessions`, sessionPayload);
      if (onSaved) onSaved(cfgPayload);
      // Close modal — caller's onRefresh will reload
      onClose();
      // Friendly notification (alert is fine — toast system not always wired)
      const sid = res?.data?.id || res?.data?.session?.id || '';
      window.alert(((t && t('sessionCreatedOK')) || 'Session created successfully.') + (sid ? ` (id: ${sid.slice(0, 8)}…)` : ''));
    } catch (e) {
      setError(((t && t('sessionCreateFailed')) || 'Failed to create session: ') + (e.response?.data?.detail || e.message));
    } finally {
      setCreating(false);
    }
  };

  const handleSave = async () => {
    setError('');
    const perfLanes = zones.map(z => z.performance_lane);
    if (perfLanes.some(l => l == null || l < 1 || l > totalLanes)) {
      setError((t && t('perfLaneRequired')) || `Each zone must have a performance lane between 1 and ${totalLanes}.`);
      return;
    }
    if (zoneCount === 2 && perfLanes[0] === perfLanes[1]) {
      setError((t && t('perfLaneConflict')) || 'Performance lane must be different for zone 1 and zone 2.');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        zone_count: zoneCount,
        zones: zones.map(z => ({
          zone_number: z.zone_number,
          performance_lane: parseInt(z.performance_lane),
          warmup_lane: z.warmup_lane == null ? null : parseInt(z.warmup_lane),
          transit_lane: z.transit_lane == null ? null : parseInt(z.transit_lane),
          assigned_user_ids: z.assigned_user_ids || [],
        }))
      };
      const res = await axios.put(`${API_BASE_URL}/api/competitions/${competition.id}/zone-config`, payload);
      if (onSaved) onSaved(res.data.zone_config);
      onClose();
    } catch (e) {
      setError('Failed to save: ' + (e.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  const labels = ROLE_LABEL(t);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-gradient-to-br from-slate-950 via-blue-950 to-cyan-950 border-cyan-700/40 text-white max-w-5xl max-h-[92vh] overflow-y-auto" data-testid="zone-config-modal">
        <DialogHeader>
          <DialogTitle className="text-cyan-200 flex items-center gap-2">
            <Settings size={20} className="text-cyan-400" />
            {(t && t('configureZones')) || 'Configure Zones'} — {competition?.name}
          </DialogTitle>
          <p className="text-cyan-400/80 text-sm">
            {(t && t('dragDropInstructions')) || `Drag the role badges below onto the pool lanes (${poolLength}m × ${poolWidth}m, ${totalLanes} lanes). Performance lanes must be unique; warmup/transit can be shared.`}
          </p>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="animate-spin mr-2" /> {(t && t('loading')) || 'Loading...'}
          </div>
        ) : (
          <div className="space-y-5">
            {/* Zone Count Selector */}
            <div className="bg-slate-900/40 border border-cyan-700/30 rounded-lg p-4">
              <p className="text-cyan-200 text-sm font-semibold mb-3">{(t && t('numberOfZones')) || 'Number of zones'}</p>
              <div className="flex gap-3">
                {[1, 2].map(n => (
                  <button
                    key={n}
                    onClick={() => setZoneCount(n)}
                    className={`flex-1 py-3 px-4 rounded-lg border-2 text-base font-semibold transition-all ${zoneCount === n ? 'border-cyan-400 bg-cyan-900/40 text-cyan-100' : 'border-slate-600/50 bg-slate-800/30 text-slate-400 hover:border-cyan-700/50'}`}
                    data-testid={`zone-count-${n}`}
                  >
                    {n} {(t && t('zone')) || 'zone'}{n > 1 ? 's' : ''}
                    <p className="text-xs font-normal mt-1 text-current opacity-75">
                      {n === 1 ? (t && t('zone1Desc') || 'Sequential — one athlete at a time') : (t && t('zone2Desc') || 'Parallel — two athletes start at the same OT')}
                    </p>
                  </button>
                ))}
              </div>
            </div>

            {/* Role Palette (draggable chips) */}
            <div className="bg-slate-900/50 border border-cyan-700/30 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <p className="text-cyan-200 text-sm font-semibold">
                  {(t && t('rolePalette')) || 'Role palette'}
                  <span className="text-cyan-400/60 text-xs font-normal ml-2">{(t && t('dragToLane')) || '(drag onto a lane)'}</span>
                </p>
                <Button onClick={resetAll} variant="ghost" size="sm" className="text-slate-400 hover:text-white" data-testid="reset-all-btn">
                  <RotateCcw size={12} className="mr-1" /> {(t && t('resetAll')) || 'Reset all'}
                </Button>
              </div>
              <div className="space-y-2">
                {zones.map((z) => (
                  <div key={z.zone_number} className="flex items-center gap-2 flex-wrap">
                    <span className={`text-xs font-bold w-14 ${z.zone_number === 1 ? 'text-blue-300' : 'text-emerald-300'}`}>{(t && t('zone')) || 'Zone'} {z.zone_number}:</span>
                    {['perf', 'warmup', 'transit'].map(role => (
                      <DraggableRoleChip
                        key={role}
                        zoneNumber={z.zone_number}
                        role={role}
                        label={labels[role]}
                        currentLane={z[FIELD_KEY[role]]}
                        onClear={() => clearRole(z.zone_number, role)}
                        isDragging={draggingFrom?.zoneNumber === z.zone_number && draggingFrom?.role === role}
                        onDragStart={onDragStart}
                        onDragEnd={onDragEnd}
                      />
                    ))}
                  </div>
                ))}
              </div>
            </div>

            {/* Pool diagram (drop targets) */}
            <div className="bg-gradient-to-b from-cyan-950/40 to-blue-950/40 border border-cyan-700/30 rounded-xl p-4">
              <div className="flex items-center justify-between mb-3">
                <p className="text-cyan-200 text-sm font-semibold flex items-center gap-2">
                  <Waves size={16} className="text-cyan-400" />
                  {(t && t('poolDiagram')) || 'Pool Diagram'}
                </p>
                <span className="text-cyan-400/70 text-xs">{poolLength}m × {poolWidth}m · {totalLanes} {(t && t('lanes')) || 'lanes'}</span>
              </div>
              <div className="space-y-2">
                {Array.from({ length: totalLanes }, (_, i) => i + 1).map(laneNumber => (
                  <PoolLane
                    key={laneNumber}
                    laneNumber={laneNumber}
                    assignments={laneAssignments[laneNumber] || []}
                    onDragOver={setHoverLane}
                    onDrop={onDropToLane}
                    onClearAll={() => clearLane(laneNumber)}
                    isHighlighted={draggingFrom != null && hoverLane === laneNumber}
                    t={t}
                  />
                ))}
              </div>
            </div>

            {/* Per-zone user attribution */}
            <div className={`grid ${zoneCount === 2 ? 'grid-cols-2' : 'grid-cols-1'} gap-4`}>
              {zones.map((zone, idx) => (
                <div
                  key={idx}
                  className={`rounded-xl border-2 p-4 ${zone.zone_number === 1 ? 'border-blue-600/40 bg-blue-950/30' : 'border-emerald-600/40 bg-emerald-950/30'}`}
                  data-testid={`zone-config-${zone.zone_number}`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <h3 className={`text-base font-bold ${zone.zone_number === 1 ? 'text-blue-200' : 'text-emerald-200'}`}>
                      {(t && t('zone')) || 'Zone'} {zone.zone_number}
                    </h3>
                    <div className="flex gap-1.5 text-[10px] flex-wrap">
                      <Badge className={`${zone.performance_lane ? 'bg-cyan-700' : 'bg-red-700/60'} text-white border-none`}>
                        PERF: {zone.performance_lane ?? '—'}
                      </Badge>
                      <Badge className={`${zone.warmup_lane ? 'bg-amber-700' : 'bg-slate-700'} text-white border-none`}>
                        W: {zone.warmup_lane ?? '—'}
                      </Badge>
                      <Badge className={`${zone.transit_lane ? 'bg-purple-700' : 'bg-slate-700'} text-white border-none`}>
                        T: {zone.transit_lane ?? '—'}
                      </Badge>
                    </div>
                  </div>
                  <div>
                    <label className="text-xs text-slate-300 font-medium flex items-center gap-1 mb-2">
                      <Users size={12} /> {(t && t('assignedUsers')) || 'Assigned operators'}
                    </label>
                    {users.length === 0 ? (
                      <p className="text-xs text-slate-500 italic">{(t && t('noUsersToAssign')) || 'No users to assign — create accounts first.'}</p>
                    ) : (
                      <div className="space-y-1 max-h-40 overflow-y-auto">
                        {users.filter(u => u.role !== 'guest').map(u => (
                          <label key={u.id} className="flex items-center gap-2 text-xs text-slate-300 cursor-pointer hover:text-white py-0.5">
                            <input
                              type="checkbox"
                              checked={(zone.assigned_user_ids || []).includes(u.id)}
                              onChange={() => toggleUser(idx, u.id)}
                              className="rounded"
                              data-testid={`zone-${zone.zone_number}-user-${u.id}`}
                            />
                            {u.display_name || u.username}
                            <Badge variant="outline" className="text-[9px] py-0 ml-auto border-slate-600 text-slate-400">{u.role}</Badge>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {error && <div className="bg-red-950/40 border border-red-600/40 rounded p-3 text-red-200 text-sm" data-testid="zone-config-error">{error}</div>}

            {/* Session Preview */}
            <div className="bg-gradient-to-br from-indigo-950/50 to-violet-950/40 border border-indigo-700/40 rounded-xl p-4">
              <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <p className="text-indigo-200 text-sm font-semibold flex items-center gap-2">
                  <Eye size={16} className="text-indigo-400" />
                  {(t && t('sessionPreview')) || 'Session preview'}
                  <span className="text-indigo-400/60 text-xs font-normal">{(t && t('sessionPreviewDesc')) || '— see what the running order will look like'}</span>
                </p>
                <div className="flex items-center gap-2 flex-wrap">
                  {availableEventTypes.length > 0 ? (
                    <select
                      value={previewEventType}
                      onChange={(e) => setPreviewEventType(e.target.value)}
                      className="bg-slate-900 border border-indigo-600/60 text-white rounded px-2 py-1.5 text-sm h-8"
                      data-testid="preview-event-type"
                    >
                      {availableEventTypes.map(code => (
                        <option key={code} value={code}>{code}</option>
                      ))}
                    </select>
                  ) : (
                    <span className="text-xs italic text-slate-500">{(t && t('noRegistrations')) || 'No registrations yet'}</span>
                  )}
                  <Button
                    onClick={runPreview}
                    disabled={previewLoading || availableEventTypes.length === 0}
                    size="sm"
                    className="bg-indigo-600 hover:bg-indigo-700 text-white"
                    data-testid="run-preview-btn"
                  >
                    {previewLoading ? <Loader2 className="animate-spin mr-1" size={12} /> : <Eye size={12} className="mr-1" />}
                    {(t && t('runPreview')) || 'Run preview'}
                  </Button>
                </div>
              </div>

              {showPreview && previewData && (
                <div className="space-y-3" data-testid="preview-result">
                  {previewData.warning ? (
                    <p className="text-amber-300 text-sm italic">{previewData.warning}</p>
                  ) : (
                    <>
                      {/* Stats */}
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                        <div className="bg-slate-900/60 rounded-lg p-3 border border-indigo-700/30">
                          <p className="text-indigo-400 text-[11px] uppercase tracking-wide flex items-center gap-1"><Users size={10} /> {(t && t('athletes')) || 'Athletes'}</p>
                          <p className="text-white font-bold text-xl font-mono mt-1">{previewData.registrations_count}</p>
                        </div>
                        <div className="bg-slate-900/60 rounded-lg p-3 border border-indigo-700/30">
                          <p className="text-indigo-400 text-[11px] uppercase tracking-wide flex items-center gap-1"><Calendar size={10} /> {(t && t('otSlots')) || 'OT slots'}</p>
                          <p className="text-white font-bold text-xl font-mono mt-1">{previewData.ot_count}</p>
                        </div>
                        <div className="bg-slate-900/60 rounded-lg p-3 border border-indigo-700/30">
                          <p className="text-indigo-400 text-[11px] uppercase tracking-wide flex items-center gap-1"><Zap size={10} /> {(t && t('simultaneousStarts')) || 'Simultaneous'}</p>
                          <p className="text-white font-bold text-xl font-mono mt-1">{previewData.simultaneous_count}</p>
                        </div>
                        <div className="bg-slate-900/60 rounded-lg p-3 border border-indigo-700/30">
                          <p className="text-indigo-400 text-[11px] uppercase tracking-wide flex items-center gap-1"><Clock size={10} /> {(t && t('totalDuration')) || 'Total duration'}</p>
                          <p className="text-white font-bold text-xl font-mono mt-1">{previewData.total_duration_minutes} <span className="text-xs text-slate-400">min</span></p>
                        </div>
                      </div>

                      {/* Timeline */}
                      <div className="max-h-72 overflow-y-auto bg-slate-950/50 rounded-lg border border-indigo-800/30 p-3">
                        <div className="space-y-1.5">
                          {previewByOT.map(({ ot, athletes }, idx) => {
                            const otTime = new Date(ot);
                            const timeStr = otTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
                            return (
                              <div key={idx} className="flex items-stretch gap-2 bg-slate-900/40 rounded border border-slate-700/40 p-2" data-testid={`preview-ot-${idx}`}>
                                <div className="flex flex-col items-center justify-center bg-yellow-700/30 border border-yellow-500/30 rounded px-2 py-1 min-w-[68px]">
                                  <span className="text-[9px] text-yellow-400 font-semibold uppercase">OT #{idx + 1}</span>
                                  <span className="text-yellow-200 font-mono text-base font-bold">{timeStr}</span>
                                </div>
                                <div className="flex-1 grid gap-1.5" style={{ gridTemplateColumns: athletes.length > 1 ? '1fr 1fr' : '1fr' }}>
                                  {athletes.map((a, k) => (
                                    <div
                                      key={k}
                                      className={`flex items-center gap-2 px-2.5 py-1.5 rounded border ${a.zone_number === 1 ? 'border-blue-500/40 bg-blue-900/30' : 'border-emerald-500/40 bg-emerald-900/30'}`}
                                      data-testid={`preview-athlete-${idx}-z${a.zone_number}`}
                                    >
                                      <Badge className={`text-[10px] py-0 ${a.zone_number === 1 ? 'bg-blue-600' : 'bg-emerald-600'} text-white border-none`}>Z{a.zone_number}</Badge>
                                      <span className="text-white text-sm font-semibold flex-1 truncate">{a.freediver_name}</span>
                                      {a.nationality && <span className="text-slate-400 text-xs">{a.nationality}</span>}
                                      <span className="text-teal-300 font-mono text-xs">AP {a.announced_performance ?? '—'}</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>

                      <p className="text-indigo-400/70 text-xs italic text-center">
                        {(t && t('previewNote')) || 'Preview only — running order will be generated when an event session is created.'}
                      </p>

                      {/* Create Session — inline form (collapsible) */}
                      <div className="mt-3 border-t border-indigo-800/40 pt-3">
                        {!showCreateForm ? (
                          <div className="flex items-center justify-between gap-3 flex-wrap">
                            <p className="text-sm text-indigo-200/90">
                              {(t && t('createSessionFromPreview')) || 'Happy with the preview? Create the session directly:'}
                            </p>
                            <Button
                              onClick={() => setShowCreateForm(true)}
                              className="bg-emerald-600 hover:bg-emerald-700 text-white"
                              size="sm"
                              data-testid="open-create-session-form"
                            >
                              <PlayCircle size={14} className="mr-1.5" />
                              {(t && t('createSession')) || 'Create session'}
                            </Button>
                          </div>
                        ) : (
                          <div className="bg-emerald-950/40 border border-emerald-700/40 rounded-lg p-4 space-y-3" data-testid="create-session-form">
                            <p className="text-emerald-200 text-sm font-semibold flex items-center gap-2">
                              <PlayCircle size={14} /> {(t && t('newLiveSession')) || 'New live session'}
                            </p>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                              <div>
                                <label className="text-xs text-emerald-200/80 block mb-1">{(t && t('sessionName')) || 'Session name'} *</label>
                                <input
                                  type="text"
                                  value={createForm.name}
                                  onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                                  placeholder={`${previewEventType} - Session 1`}
                                  className="w-full bg-slate-900 border border-emerald-700/50 text-white rounded px-2 py-1.5 text-sm h-9"
                                  data-testid="cs-name"
                                />
                              </div>
                              <div className="grid grid-cols-2 gap-2">
                                <div>
                                  <label className="text-xs text-emerald-200/80 block mb-1">{(t && t('date')) || 'Date'} *</label>
                                  <input
                                    type="date"
                                    value={createForm.start_date}
                                    onChange={(e) => setCreateForm({ ...createForm, start_date: e.target.value })}
                                    className="w-full bg-slate-900 border border-emerald-700/50 text-white rounded px-2 py-1.5 text-sm h-9"
                                    data-testid="cs-date"
                                  />
                                </div>
                                <div>
                                  <label className="text-xs text-emerald-200/80 block mb-1">{(t && t('time')) || 'Time'} *</label>
                                  <input
                                    type="time"
                                    value={createForm.start_time}
                                    onChange={(e) => setCreateForm({ ...createForm, start_time: e.target.value })}
                                    className="w-full bg-slate-900 border border-emerald-700/50 text-white rounded px-2 py-1.5 text-sm h-9"
                                    data-testid="cs-time"
                                  />
                                </div>
                              </div>
                              <div>
                                <label className="text-xs text-emerald-200/80 block mb-1">{(t && t('intervalDuration')) || 'Interval per passage'} (min)</label>
                                <input
                                  type="number"
                                  min="1"
                                  value={createForm.interval_duration}
                                  onChange={(e) => setCreateForm({ ...createForm, interval_duration: e.target.value })}
                                  className="w-full bg-slate-900 border border-emerald-700/50 text-white rounded px-2 py-1.5 text-sm h-9"
                                  data-testid="cs-interval"
                                />
                              </div>
                              <div>
                                <label className="text-xs text-emerald-200/80 block mb-1">{(t && t('preparationDuration')) || 'Preparation/warm-up'} (min)</label>
                                <input
                                  type="number"
                                  min="1"
                                  value={createForm.preparation_duration}
                                  onChange={(e) => setCreateForm({ ...createForm, preparation_duration: e.target.value })}
                                  className="w-full bg-slate-900 border border-emerald-700/50 text-white rounded px-2 py-1.5 text-sm h-9"
                                  data-testid="cs-prep"
                                />
                              </div>
                            </div>
                            <p className="text-emerald-300/70 text-[11px] italic">
                              {(t && t('createSessionNote')) || 'Saving will persist your zone configuration and immediately create the live session.'}
                            </p>
                            <div className="flex justify-end gap-2">
                              <Button onClick={() => setShowCreateForm(false)} variant="outline" className="border-slate-600 text-slate-300" size="sm" disabled={creating} data-testid="cs-cancel">
                                {(t && t('cancel')) || 'Cancel'}
                              </Button>
                              <Button onClick={createSession} className="bg-emerald-600 hover:bg-emerald-700 text-white" size="sm" disabled={creating} data-testid="cs-submit">
                                {creating ? <Loader2 className="animate-spin mr-1" size={12} /> : <PlayCircle size={12} className="mr-1" />}
                                {(t && t('createNow')) || 'Create now'}
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} className="border-slate-600 text-slate-300" disabled={saving} data-testid="zone-config-cancel">
            {(t && t('cancel')) || 'Cancel'}
          </Button>
          <Button onClick={handleSave} className="bg-cyan-600 hover:bg-cyan-700 text-white" disabled={saving || loading} data-testid="zone-config-save">
            {saving ? <Loader2 className="animate-spin mr-2" size={14} /> : <Save className="mr-2" size={14} />}
            {(t && t('saveConfiguration')) || 'Save Configuration'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ZoneConfigModal;
